import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  #abortController = new AbortController();

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.syncCartCount, {
      signal: this.#abortController.signal,
    });

    this.syncCartCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  syncCartCount = async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to load cart: ${response.status}`);

      const cart = await response.json();
      const itemCount = Number(cart.item_count) || 0;

      this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
      this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
      this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0);
      this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
      this.currentCartCount = itemCount;
    } catch (error) {
      console.error(error);
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
